import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { getAdminRoles } from "../../../sagas/actions";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import QuickStartModal from "./QuickStartModal";
import { ADMIN_TAB_RESET_EVENT } from "../../../hooks/usePersistentTabState";
import { ADMINCENTRAL_IMAGE_BASE_URL, ISAFE_DIRECT_ENDPOINT } from "../../../util/constant";
import API from "../../../util/api";

// breakpoints
const breakpoints = {
  tablet: 768,
  phone: 480,
};

// role sets
const ISAFE_ROLES = new Set([
  "isafe_accountant",
  "isafe_moderator",
  "isafe_salesperson",
  "isafe_admin",
  "root",
]);
const SUBSCRIPTION_ROLES = new Set([
  "subscription_access",
  "subscription_accountant",
  "subscription_admin",
  "subscription_implementor",
  "subscription_salesperson",
]);

const MenuContainer = styled.div`
  width: 100%;
  padding: 32px 24px 40px;
  box-sizing: border-box;
  @media (max-width: ${breakpoints.tablet}px) {
    padding: 24px 16px;
  }
  @media (max-width: ${breakpoints.phone}px) {
    padding: 20px 12px;
  }
`;

const HeadingRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const MenuText = styled.div`
  font-size: 36px;
  color: ${({ colors }) => colors.titleText};
  font-weight: 600;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 24px;
  }
`;

const QuickStartButton = styled.button`
  padding: 0.5em 1.2em;
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 0.95em;
  font-weight: bold;
  cursor: pointer;
  align-self: flex-end;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const QuestionRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: #000;
  font-size: 16px;

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 14px;
    gap: 10px;
  }
`;

const ScrollWrapper = styled.div`
  position: relative;
  overflow: visible;

  @media (max-width: ${breakpoints.tablet}px) {
    /* give extra padding so arrows sit in blank space */
    padding: 0 20px;
  }
`;

const MenuAccess = styled.div`
  display: flex;
  gap: 15px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin: 16px 0;

  @media (max-width: ${breakpoints.tablet}px) {
    gap: 16px;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0 10px;
    -webkit-overflow-scrolling: touch;

    scrollbar-width: auto;
    scrollbar-color: #ccc #f1f1f1;

    &::-webkit-scrollbar {
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #aaa;
    }
  }
`;

const ArrowButton = styled.button`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  z-index: 2;
  color: #fff;
  font-size: 18px;

  @media (max-width: ${breakpoints.tablet}px) {
    display: block;
  }

  ${({ left }) =>
    left
      ? `
    left: 0;
  `
      : `
    right: 0;
  `}
`;

const MenuCard = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  text-align: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  &:hover::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.2);
    border-radius: 10px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex: 0 0 auto;
    width: 20vw;
    max-width: 200px;
  }
`;

const MenuCardExternal = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  text-align: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  &:hover::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.2);
    border-radius: 10px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    flex: 0 0 auto;
    width: 20vw;
    max-width: 200px;
  }
`;

const MenuIcon = styled.img`
  width: 10vw;
  max-width: 130px;
  height: auto;
  border-radius: 10px;

  @media (max-width: ${breakpoints.phone}px) {
    width: 100%;
  }
`;

export default function AdminCentralDashboardMenu({ colors }) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [isLaunchingMyok, setIsLaunchingMyok] = useState(false);
  const accessToken = useSelector(
    (s) => s.auth?.loginResponse?.access_token || s.auth?.token
  );
  const userEmail = useSelector((s) => s.auth?.user?.email || "");

  useEffect(() => {
    dispatch(getAdminRoles());
  }, [dispatch]);

  const userRoles = useSelector((s) => s.auth.user.roles) || [];
  const become = useSelector((s) => s.auth.become);
  const isISAFEAdmin = userRoles.some((r) => ISAFE_ROLES.has(r.id));
  const isSubscriptionAdmin =
    !isISAFEAdmin && userRoles.some((r) => SUBSCRIPTION_ROLES.has(r.id));

  const scrollBy = (distance) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: distance, behavior: "smooth" });
    }
  };

  const handleMenuNavClick = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(ADMIN_TAB_RESET_EVENT));
  };

  const myokBaseUrl = ISAFE_DIRECT_ENDPOINT ? `${ISAFE_DIRECT_ENDPOINT}/myok` : "";

  const handleMyokLaunch = async (event) => {
    event.preventDefault();
    handleMenuNavClick();

    if (!myokBaseUrl || isLaunchingMyok) {
      return;
    }

    if (!accessToken || !userEmail) {
      window.location.assign(myokBaseUrl);
      return;
    }

    setIsLaunchingMyok(true);
    try {
      const response = await API.encryptTokens(accessToken, userEmail);
      const encryptedToken = response?.token;
      const encryptedEmail = response?.email;

      if (encryptedToken && encryptedEmail) {
        const queryParams = new URLSearchParams({
          access_token: encryptedToken,
          email: encryptedEmail,
        });
        window.location.assign(`${myokBaseUrl}?${queryParams.toString()}`);
        return;
      }

      console.error("Unable to encrypt ISAFE Direct launch params", response);
      window.location.assign(myokBaseUrl);
    } catch (error) {
      console.error("ISAFE Direct launch encryption failed", error);
      window.location.assign(myokBaseUrl);
    } finally {
      setIsLaunchingMyok(false);
    }
  };
  const menuImageBaseUrl = ADMINCENTRAL_IMAGE_BASE_URL;

  return (
    <MenuContainer>
      <HeadingRow>
        <MenuText colors={colors}>Welcome to Your Administration Area</MenuText>
        <QuestionRow>
          <span style={{fontWeight: 600, fontSize: '16px', textShadow: "2px 2px 5px rgba(0, 0, 0, 0.11)"}}>What would you like to do?</span>
          {(become === 1 || isSubscriptionAdmin) && (
            <QuickStartButton
              colors={colors}
              onClick={() => setQuickStartOpen(true)}
            >
              Quick Start
            </QuickStartButton>
          )}
        </QuestionRow>
      </HeadingRow>

      <ScrollWrapper>
        <ArrowButton left onClick={() => scrollBy(-150)}>
          <FaChevronLeft />
        </ArrowButton>

        <MenuAccess ref={scrollRef}>
          <MenuCard
            to="/admincentral/isafeadmins/dashboard"
            onClick={handleMenuNavClick}
          >
            <MenuIcon
              src={`${menuImageBaseUrl}/dashboard-img.svg`}
              alt="Dashboard"
            />
          </MenuCard>

          {(become === 1 || isSubscriptionAdmin) && (
            <>
              <MenuCard to="/admincentral/teachers" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/teachers-img.svg`}
                  alt="Teachers"
                />
              </MenuCard>
              <MenuCard to="/admincentral/admins" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/administrators-img.svg`}
                  alt="Administrators"
                />
              </MenuCard>
              <MenuCard to="/admincentral/reports" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/reports-img.svg`}
                  alt="Reports"
                />
              </MenuCard>
              <MenuCard to="/admincentral/subscriptions" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/subscriptions-img.svg`}
                  alt="Subscriptions"
                />
              </MenuCard>
              <MenuCardExternal
                href={myokBaseUrl || "#"}
                onClick={handleMyokLaunch}
              >
                <MenuIcon
                  src={`${menuImageBaseUrl}/aup-cloud-services-img.svg`}
                  alt="ISAFE Direct"
                />
              </MenuCardExternal>
              <MenuCard to="/admincentral/resources" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/resources-img.svg`}
                  alt="Resources"
                />
              </MenuCard>
              <MenuCard to="/admincentral/faq" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/user-guidefaq-img.svg`}
                  alt="User Guide"
                />
              </MenuCard>
            </>
          )}

          {isISAFEAdmin && become === 0 && (
            <>
              <MenuCard
                to="/admincentral/subscriptions/search"
                onClick={handleMenuNavClick}
              >
                <MenuIcon
                  src={`${menuImageBaseUrl}/subscriptions-img.svg`}
                  alt="Subscriptions"
                />
              </MenuCard>
              <MenuCard to="/admincentral/isafeadmins" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/administrators-img.svg`}
                  alt="Administrators"
                />
              </MenuCard>
              <MenuCard to="/admincentral/orgs/search" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/directory-img.svg`}
                  alt="Directory"
                />
              </MenuCard>
              <MenuCard to="/admincentral/other" onClick={handleMenuNavClick}>
                <MenuIcon
                  src={`${menuImageBaseUrl}/other-img.svg`}
                  alt="Other"
                />
              </MenuCard>
            </>
          )}
        </MenuAccess>

        <ArrowButton onClick={() => scrollBy(150)}>
          <FaChevronRight />
        </ArrowButton>
      </ScrollWrapper>

      <QuickStartModal
        show={quickStartOpen}
        colors={colors}
        onClose={() => setQuickStartOpen(false)}
      />
    </MenuContainer>
  );
}
